import { useEffect, useContext } from 'react'
import { useCurrentUser } from 'context'
import { useMediaQuery, useMantineTheme } from 'hooks'
import { useFeatureToggle, useFeatureToggleLoading } from 'hooks/useFeatureToggle'
import { useArcTranslation } from 'hooks'
import { arcHeroContentOffset } from 'components/ArcPageShell/constants'
import { carouselDashboard, useCaroselStyles } from 'components/Carousel'
import {
  Space,
  Flex,
  Text,
  Carousel,
  GroupCard,
  AssessmentModal,
  NextTaskCard,
  DashboardHeader,
} from 'components'
import { StudentDashboardContext } from '../../context/StudentDashboardContext'
import { IconChecklist, IconUsers } from '@tabler/icons-react'
import { StudentDashBoardOnLoad } from '../StudentDashboardSkeleton'
import { StudentProjectsSection } from '../StudentProjectsSection/StudentProjectsSection'
import { getMyGroups, getMySessionMeetings } from 'api/studentsDashboard/service'
import { MeetingAssessmentForStudent } from 'components/MeetingAssessmentForStudent'
import { IGroup } from 'interfaces'

export const StudentDashboard = () => {
  const {
    groups,
    setGroups,
    lessons,
    setLessons,
    loading,
    setLoading,
    handleJournalClick,
    assessmentID,
    assessmentModalOpened,
    assessmentModalClose,
    modalTitle,
    setShowConfirmationModal,
    updateModalTitle,
    setModalHasChanges,
    showConfirmationModal,
    UpdateShowModal,
    projectURL,
  } = useContext(StudentDashboardContext)
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { currentUser } = useCurrentUser()
  const { t } = useArcTranslation()
  const { classes } = useCaroselStyles()
  const isArcEnabled = useFeatureToggle('arc-mosaico-project') as boolean
  const isFeaturesLoading = useFeatureToggleLoading()

  const loadDashBoardData = async () => {
    const [groupsResponse, meetingsResponse] = await Promise.all([getMyGroups(), getMySessionMeetings()])
    setGroups(groupsResponse)
    setLessons(meetingsResponse)
    setLoading(false)
  }

  useEffect(() => {
    setLoading(true)
    loadDashBoardData()
  }, [])

  if (loading) {
    return <StudentDashBoardOnLoad />
  }

  const showActivitiesIndicator: boolean = (isMobile && lessons.length > 2) || lessons.length > 3
  const showGroupsIndicator: boolean = (isMobile && groups.length > 2) || groups.length > 3
  // In Arc mode the "Next tasks" section is replaced by the "My tasks" screen
  // (AR-543). Waiting for the flags avoids flashing the section for Arc users.
  const showNextTasks = !isFeaturesLoading && !isArcEnabled && lessons.length > 0

  return (
    <>
      <AssessmentModal
        assessmentModalOpened={assessmentModalOpened}
        assessmentModalClose={assessmentModalClose}
        modalTitle={modalTitle}
        content={
          <MeetingAssessmentForStudent
            id={assessmentID}
            onClose={() => {
              setShowConfirmationModal(false)
              assessmentModalClose()
            }}
            updateModaTitle={updateModalTitle}
            setModalHasChanges={setModalHasChanges}
            toggleConfirmationModal={showConfirmationModal}
            UpdateShowModal={UpdateShowModal}
            projectUrl={projectURL ? projectURL : ''}
            loadDashBoardData={loadDashBoardData}
          />
        }
      />
      <DashboardHeader name={currentUser?.full_name} isMobile={isMobile} />
      {showNextTasks && (
        <>
          {/* Arc: hero clearance compensates the reduced container top padding (AR-578) */}
          <Flex mt={isArcEnabled ? arcHeroContentOffset(isMobile ? 0 : 80, isMobile) : isMobile ? 0 : 80}>
            <IconChecklist />
            <Space w='xs' />
            <Text size='lg' weight={700}>
              {t('students_next_activities')}
            </Text>
          </Flex>
          <Space h='xs' />
          <Carousel
            {...carouselDashboard}
            withIndicators={showActivitiesIndicator}
            withControls={showActivitiesIndicator}
            draggable={lessons.length > 1}
            classNames={{
              root: classes.carousel,
              controls: classes.carouselControls,
            }}
          >
            {lessons?.map((lesson, idx) => (
              <Carousel.Slide key={lesson.editReportUrl ?? idx}>
                <NextTaskCard
                  projectName={lesson.projectName}
                  lessonName={lesson.lessonName}
                  date={lesson.date}
                  time={lesson.hour}
                  canFillEditReport={lesson.canFillEditReport || false}
                  activities={lesson.activities}
                  status={lesson.status}
                  handleJournalClick={() => handleJournalClick(lesson.editReportUrl, lesson.projectUrl)}
                />
              </Carousel.Slide>
            ))}
          </Carousel>
        </>
      )}

      {/* Assigned projects (Arc only) — read-only carousel between Next tasks and My classes */}
      {!isFeaturesLoading && isArcEnabled && <StudentProjectsSection />}

      {groups.length > 0 && (
        <>
          {showNextTasks && <Space h='lg' />}
          <Flex mt={showNextTasks ? 0 : isArcEnabled ? arcHeroContentOffset(80, isMobile) : 80}>
            <IconUsers />
            <Space w='xs' />
            <Text size='lg' weight={700}>
              {t('students_my_classes')}
            </Text>
          </Flex>
          <Space h='xs' />
        </>
      )}

      <Carousel
        {...carouselDashboard}
        draggable={groups.length > 1}
        classNames={{
          root: classes.carousel,
          controls: classes.carouselControls,
        }}
        withControls={showGroupsIndicator}
        withIndicators={showGroupsIndicator}
      >
        {groups?.map((item, idx) => {
          const group = {
            id: item.group,
            name: item.group,
            grade: item.grade,
            cycle: item.cycle,
          } as IGroup

          return (
            <Carousel.Slide key={item.group ?? idx}>
              <GroupCard group={group} showLink={false} byCycle={currentUser?.by_cycle || false} />
            </Carousel.Slide>
          )
        })}
      </Carousel>
    </>
  )
}
